import React, { useMemo, useState } from "react";
import { Wallet, Clock, Building2, QrCode, Banknote, Upload, Check, X as XIcon, AlertCircle, Filter, Bell } from "lucide-react";
import { formatRupiah } from "../lib/api";
import api from "../lib/api";
import PaymentProofModal from "./PaymentProofModal";
import PaymentReviewModal from "./PaymentReviewModal";
import { toast } from "sonner";

const STATUS_META = {
  PAID:           { label: "Paid",       color: "#10B981", bg: "bg-emerald-500/15", text: "text-emerald-400", border: "border-emerald-500/30", icon: "✅" },
  PROOF_UPLOADED: { label: "Pending",    color: "#F59E0B", bg: "bg-amber-500/15",   text: "text-amber-400",   border: "border-amber-500/30",   icon: "📤" },
  UNPAID:         { label: "Unpaid",     color: "#A1A1AA", bg: "bg-secondary",      text: "text-muted-foreground", border: "border-border", icon: "⏳" },
  REJECTED:       { label: "Rejected",   color: "#EF4444", bg: "bg-destructive/15", text: "text-destructive", border: "border-destructive/30", icon: "❌" },
};

const METHOD_META = {
  transfer: { label: "Bank Transfer", icon: Building2 },
  qris:     { label: "QRIS",          icon: QrCode },
  cash:     { label: "Cash",          icon: Banknote },
};

export default function PaymentRecap({ match, players, host, isHost, currentUserId, onUpdated }) {
  const [proofOpen, setProofOpen] = useState(false);
  const [reviewEntry, setReviewEntry] = useState(null);
  const [showOnlyUnpaid, setShowOnlyUnpaid] = useState(false);
  const [showQris, setShowQris] = useState(false);
  const [reminding, setReminding] = useState(false);

  const payments = match.payments || {};
  const method = METHOD_META[match.payment_method] || METHOD_META.transfer;
  const MethodIcon = method.icon;

  // Combine player roster with payment entries
  const entries = useMemo(() => {
    const playerMap = Object.fromEntries(players.map((p) => [p.user_id, p]));
    if (host && !playerMap[host.user_id]) playerMap[host.user_id] = host;
    return match.player_ids.map((uid) => {
      const p = playerMap[uid] || { user_id: uid, name: "Unknown" };
      const pay = payments[uid] || { status: "UNPAID" };
      return { ...p, ...pay, user_id: uid, is_host: uid === match.host_id };
    });
  }, [match, players, host, payments]);

  const summary = useMemo(() => {
    const counts = { PAID: 0, PROOF_UPLOADED: 0, UNPAID: 0, REJECTED: 0 };
    entries.forEach((e) => { counts[e.status] = (counts[e.status] || 0) + 1; });
    const total = entries.length || 1;
    const paidRatio = Math.round((counts.PAID / total) * 100);
    return { counts, total, paidRatio };
  }, [entries]);

  const myEntry = entries.find((e) => e.user_id === currentUserId);
  const filteredEntries = showOnlyUnpaid
    ? entries.filter((e) => e.status === "UNPAID" || e.status === "REJECTED")
    : entries;
  const unpaidCount = entries.filter((e) => !e.is_host && (e.status === "UNPAID" || e.status === "REJECTED")).length;

  const sendReminder = async () => {
    if (!unpaidCount) return;
    setReminding(true);
    try {
      const { data } = await api.post(`/matches/${match.match_id}/payment/remind`);
      toast.success(`Reminder terkirim ke ${data.sent} pemain`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal kirim reminder");
    } finally {
      setReminding(false);
    }
  };

  const noPaymentNeeded = !match.cost_per_player && !match.total_cost;
  if (noPaymentNeeded) {
    return (
      <div className="bg-card border border-border rounded-2xl p-5 mt-4">
        <div className="flex items-center gap-2 mb-1">
          <Wallet className="h-4 w-4 text-primary" />
          <div className="font-heading text-base font-black tracking-tight">Payment Recap</div>
        </div>
        <div className="text-sm text-muted-foreground">Match ini gratis. Tidak perlu setoran 🎉</div>
      </div>
    );
  }

  return (
    <div className="mt-4" data-testid="payment-recap">
      {/* Header */}
      <div className="flex items-center justify-between px-1 mb-2">
        <div className="flex items-center gap-2">
          <Wallet className="h-4 w-4 text-primary" />
          <div className="font-heading text-base font-black tracking-tight">Payment Recap</div>
        </div>
        <div className="text-xs font-bold text-primary">{summary.paidRatio}% paid</div>
      </div>

      {/* Cost + Method card */}
      <div className="bg-card border border-border rounded-2xl p-4 relative overflow-hidden">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Per Pemain</div>
            <div className="font-heading text-2xl font-black mt-0.5">{formatRupiah(match.cost_per_player)}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Total Pot</div>
            <div className="font-heading text-2xl font-black mt-0.5 text-primary">
              {formatRupiah((match.total_cost && match.total_cost > 0) ? match.total_cost : match.cost_per_player * match.max_players)}
            </div>
          </div>
        </div>

        {/* Method + notes */}
        <div className="mt-4 bg-secondary/60 border border-border rounded-xl p-3">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
              <MethodIcon className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Metode</div>
              <div className="text-sm font-bold">{method.label}</div>
            </div>
            {match.qris_image && (
              <button
                onClick={() => setShowQris(true)}
                data-testid="show-qris"
                className="text-xs font-bold text-primary px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20"
              >
                Lihat QRIS
              </button>
            )}
          </div>
          {match.payment_notes && (
            <div className="mt-3 pt-3 border-t border-border text-sm whitespace-pre-wrap break-words">{match.payment_notes}</div>
          )}
          {match.payment_deadline && (
            <div className="mt-3 pt-3 border-t border-border flex items-center gap-1.5 text-xs text-muted-foreground">
              <Clock className="h-3.5 w-3.5" /> Deadline {new Date(match.payment_deadline).toLocaleString("id-ID", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
            </div>
          )}
        </div>

        {/* Quick stats row */}
        <div className="mt-3 grid grid-cols-3 gap-2 text-center">
          <Stat label="Paid"    value={summary.counts.PAID}            color="#10B981" />
          <Stat label="Pending" value={summary.counts.PROOF_UPLOADED}  color="#F59E0B" />
          <Stat label="Unpaid"  value={summary.counts.UNPAID + summary.counts.REJECTED} color="#A1A1AA" />
        </div>
      </div>

      {/* My status (player) */}
      {myEntry && !myEntry.is_host && (
        <MyStatusCard entry={myEntry} onUpload={() => setProofOpen(true)} cost={match.cost_per_player} />
      )}

      {/* Player list */}
      <div className="mt-4">
        <div className="flex items-center justify-between mb-2 px-1 gap-2">
          <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Status Pembayaran</div>
          <div className="flex items-center gap-1.5">
            {isHost && unpaidCount > 0 && (
              <button
                onClick={sendReminder}
                disabled={reminding}
                data-testid="remind-unpaid-btn"
                className="text-xs font-bold inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-primary text-primary-foreground glow-cyan active:scale-95 disabled:opacity-50"
              >
                <Bell className="h-3 w-3" /> {reminding ? "Mengirim..." : `Tagih (${unpaidCount})`}
              </button>
            )}
            {isHost && (
              <button
                onClick={() => setShowOnlyUnpaid((v) => !v)}
                data-testid="filter-unpaid"
                className={`text-xs font-bold inline-flex items-center gap-1 px-3 py-1.5 rounded-full border ${showOnlyUnpaid ? "bg-primary/10 border-primary/30 text-primary" : "bg-secondary border-border text-muted-foreground"}`}
              >
                <Filter className="h-3 w-3" /> Belum Bayar
              </button>
            )}
          </div>
        </div>
        <div className="space-y-2">
          {filteredEntries.length === 0 && (
            <div className="bg-card border border-border rounded-2xl p-4 text-center text-sm text-muted-foreground">
              Semua sudah lunas ✨
            </div>
          )}
          {filteredEntries.map((e) => (
            <PaymentRow
              key={e.user_id}
              entry={e}
              isHost={isHost}
              isMe={e.user_id === currentUserId}
              onUpload={() => setProofOpen(true)}
              onReview={() => setReviewEntry(e)}
            />
          ))}
        </div>
      </div>

      {/* QRIS preview */}
      {showQris && match.qris_image && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6" onClick={() => setShowQris(false)}>
          <div className="max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <div className="bg-white rounded-3xl overflow-hidden">
              <img src={match.qris_image} alt="QRIS" className="w-full" />
            </div>
            <button onClick={() => setShowQris(false)} className="mt-4 w-full px-6 py-3 rounded-full bg-secondary border border-border text-foreground font-bold">Tutup</button>
          </div>
        </div>
      )}

      {proofOpen && <PaymentProofModal matchId={match.match_id} onClose={() => setProofOpen(false)} onUpdated={onUpdated} />}
      {reviewEntry && <PaymentReviewModal matchId={match.match_id} entry={reviewEntry} onClose={() => setReviewEntry(null)} onUpdated={onUpdated} />}
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="bg-secondary/60 border border-border rounded-xl py-2">
      <div className="font-heading text-lg font-black" style={{ color }}>{value}</div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</div>
    </div>
  );
}

function MyStatusCard({ entry, onUpload, cost }) {
  const meta = STATUS_META[entry.status] || STATUS_META.UNPAID;
  const canUpload = entry.status === "UNPAID" || entry.status === "REJECTED";
  return (
    <div className={`mt-3 rounded-2xl p-4 border ${meta.border} ${meta.bg}`} data-testid="my-payment-status">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-wider opacity-80">Status Bayar Kamu</div>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-lg">{meta.icon}</span>
            <span className={`font-heading text-lg font-black ${meta.text}`}>{meta.label}</span>
          </div>
          {entry.status === "REJECTED" && entry.rejection_note && (
            <div className="mt-2 text-xs text-destructive/90">Catatan host: {entry.rejection_note}</div>
          )}
          {entry.status === "PROOF_UPLOADED" && (
            <div className="mt-2 text-xs opacity-80">Menunggu host approve</div>
          )}
        </div>
        {canUpload && (
          <button
            onClick={onUpload}
            data-testid="upload-proof-btn"
            className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-primary text-primary-foreground font-bold text-sm glow-cyan active:scale-95"
          >
            <Upload className="h-3.5 w-3.5" /> Upload Bukti
          </button>
        )}
      </div>
    </div>
  );
}

function PaymentRow({ entry, isHost, isMe, onUpload, onReview }) {
  const meta = STATUS_META[entry.status] || STATUS_META.UNPAID;
  const letter = (entry.name || "?")[0]?.toUpperCase();
  return (
    <div className="bg-card border border-border rounded-2xl p-3 flex items-center gap-3" data-testid={`payment-row-${entry.user_id}`}>
      <div className="h-10 w-10 rounded-full bg-secondary border border-border flex items-center justify-center overflow-hidden shrink-0">
        {entry.picture ? <img src={entry.picture} alt="" className="h-full w-full object-cover" /> : <span className="font-bold text-sm">{letter}</span>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-sm truncate flex items-center gap-1">
          {entry.name}
          {entry.is_host && <span className="text-[9px] font-bold uppercase tracking-wider text-primary bg-primary/10 px-1.5 py-0.5 rounded-full">host</span>}
          {isMe && !entry.is_host && <span className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground bg-secondary px-1.5 py-0.5 rounded-full">kamu</span>}
        </div>
        <div className="text-xs text-muted-foreground flex items-center gap-1.5 mt-0.5">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${meta.bg} ${meta.text} border ${meta.border}`}>
            {meta.icon} {meta.label}
          </span>
          {entry.uploaded_at && <span className="text-[10px]">{new Date(entry.uploaded_at).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" })}</span>}
        </div>
      </div>
      {/* Actions */}
      {!entry.is_host && (
        <>
          {isHost && entry.status === "PROOF_UPLOADED" && (
            <button
              onClick={onReview}
              data-testid={`review-${entry.user_id}`}
              className="shrink-0 px-3 py-1.5 rounded-full bg-primary text-primary-foreground text-xs font-bold active:scale-95"
            >
              Review
            </button>
          )}
          {isHost && entry.status === "PAID" && (
            <button
              onClick={onReview}
              data-testid={`view-${entry.user_id}`}
              className="shrink-0 px-3 py-1.5 rounded-full bg-secondary border border-border text-xs font-bold active:scale-95"
            >
              Lihat
            </button>
          )}
          {isMe && (entry.status === "UNPAID" || entry.status === "REJECTED") && (
            <button
              onClick={onUpload}
              data-testid={`upload-${entry.user_id}`}
              className="shrink-0 px-3 py-1.5 rounded-full bg-primary text-primary-foreground text-xs font-bold active:scale-95"
            >
              Upload
            </button>
          )}
        </>
      )}
    </div>
  );
}
